import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AppHeader } from "@/components/AppHeader";
import { ItemRow } from "@/components/ItemRow";
import { AddItemDialog } from "@/components/AddItemDialog";
import { FinalizePanel } from "@/components/FinalizePanel";
import { useApi, errMsg, uploadPhoto, fmtDate } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { ConfirmRemoveDialog } from "@/components/ConfirmRemoveDialog";
import { NameDialog } from "@/components/NameDialog";
import { FolderPlus, Trash2 } from "lucide-react";

const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-");

export default function VehiclePage() {
  const { sid, vk } = useParams();
  const api = useApi();
  const me = useMe();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [addSection, setAddSection] = useState(false);
  const base = `/shows/${sid}/vehicles/${vk}`;

  useEffect(() => {
    api.get(base).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [api, base]);

  const run = useCallback(async (fn, okMsg) => {
    try {
      const r = await fn();
      setData(r.data || r);
      if (okMsg) toast.success(okMsg);
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen">
        <AppHeader back={`/show/${sid}`} title="Carregando..." />
        <div className="max-w-3xl mx-auto px-4 py-8 space-y-3">{[0, 1, 2, 3].map((i) => <div key={i} className="h-16 rounded-lg shimmer" />)}</div>
      </div>
    );
  }

  const { show, vehicle } = data;
  const locked = !!vehicle.finalized_at;
  const items = vehicle.sections.flatMap((s) => s.items);
  const checked = items.filter((i) => i.checked).length;
  const pct = items.length ? Math.round((checked / items.length) * 100) : 0;

  const toggle = (item) => {
    setData((d) => ({ ...d, vehicle: { ...d.vehicle, sections: d.vehicle.sections.map((s) => ({ ...s, items: s.items.map((i) => (i.id === item.id ? { ...i, checked: !i.checked } : i)) })) } }));
    run(() => api.patch(`${base}/items/${item.id}`, { checked: !item.checked })).then((ok) => { if (!ok) api.get(base).then((r) => setData(r.data)); });
  };

  return (
    <div className="min-h-screen pb-10">
      <AppHeader back={`/show/${sid}`} title={vehicle.name} subtitle={`${show.city} · ${fmtDate(show.date)}`} />
      <div className="sticky top-16 z-20 bg-[#0B0C10]/90 backdrop-blur-md border-b border-[#2A2E39]">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="flex-1 h-2 rounded-full bg-[#15181E] overflow-hidden">
            <div className={`h-full transition-[width] duration-500 ${pct === 100 ? "bg-[#00E676]" : "bg-[#8A9440]"}`} style={{ width: `${pct}%` }} />
          </div>
          <span className="font-mono text-sm text-white" data-testid="vehicle-progress-text">{checked}/{items.length}</span>
          {locked && <span className="font-mono text-[10px] uppercase tracking-widest border border-[#00E676]/60 text-[#00E676] rounded px-1.5 py-0.5" data-testid="vehicle-locked-badge">Finalizado</span>}
        </div>
      </div>
      <main className="max-w-3xl mx-auto px-4 py-6 space-y-8">
        {vehicle.sections.map((s) => {
          const sc = s.items.filter((i) => i.checked).length;
          return (
            <section key={s.name} className="space-y-3" data-testid={`section-${slug(s.name)}`}>
              <div className="flex items-center justify-between gap-3 border-b border-[#2A2E39] pb-2">
                <h2 className="font-display text-lg font-bold uppercase tracking-wider text-slate-100">{s.name}</h2>
                <div className="flex items-center gap-1">
                  <span className={`font-mono text-xs ${sc === s.items.length && sc > 0 ? "text-[#00E676]" : "text-slate-500"}`}>{sc}/{s.items.length}</span>
                  {!locked && (
                    <button type="button" onClick={() => setSectionToDelete(s)} data-testid={`section-delete-btn-${slug(s.name)}`} className="w-9 h-9 grid place-items-center rounded-md text-slate-500 hover:text-[#FF3838] hover:bg-[#FF3838]/10 transition-colors" title="Remover categoria">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              <ul className="space-y-2">
                {s.items.map((item) => (
                  <ItemRow key={item.id} item={item} locked={locked}
                    onToggle={() => toggle(item)}
                    onQty={(n) => run(() => api.patch(`${base}/items/${item.id}`, n === null ? { clear_qty: true } : { qty_counted: n }))}
                    onPhoto={(f) => run(() => uploadPhoto(api, `${base}/items/${item.id}/photos`, f), "Foto adicionada")}
                    onDeletePhoto={(fid) => run(() => api.delete(`${base}/items/${item.id}/photos/${fid}`), "Foto removida")}
                    onDelete={() => setToDelete(item)} />
                ))}
              </ul>
              {!locked && <AddItemDialog section={s.name} testId={`add-item-btn-${slug(s.name)}`} onAdd={(body) => run(() => api.post(`${base}/items`, body), "Item adicionado")} />}
            </section>
          );
        })}
        {vehicle.sections.length === 0 && (
          <p className="rounded-xl border border-dashed border-[#2A2E39] p-8 text-center text-slate-400" data-testid="sections-empty">Nenhuma categoria ainda. Adicione a primeira para começar a lista.</p>
        )}
        {!locked && (
          <button type="button" onClick={() => setAddSection(true)} data-testid="add-section-btn"
            className="w-full min-h-[52px] rounded-lg border border-[#636b2f] text-[#A9B26A] hover:bg-[#636b2f]/20 flex items-center justify-center gap-2 font-display text-base font-bold uppercase tracking-wider transition-colors">
            <FolderPlus className="w-5 h-5" /> Adicionar categoria
          </button>
        )}
        <FinalizePanel vehicle={vehicle} pending={items.length - checked} isAdmin={me.role === "admin"}
          onPhoto={(f) => run(() => uploadPhoto(api, `${base}/photos`, f), "Foto final adicionada")}
          onDeletePhoto={(fid) => run(() => api.delete(`${base}/photos/${fid}`), "Foto removida")}
          onFinalize={(notes) => run(() => api.post(`${base}/finalize`, { notes }), `${vehicle.name} finalizado`).then((ok) => { if (ok) navigate(`/show/${sid}`); })}
          onReopen={() => run(() => api.post(`${base}/reopen`), "Checklist reaberto")} />
      </main>
      <ConfirmRemoveDialog open={!!toDelete} testId="delete-item" title={`Remover "${toDelete?.name}"?`} description="O item sai do checklist deste show."
        onCancel={() => setToDelete(null)}
        onConfirm={(fromTemplate) => { run(() => api.delete(`${base}/items/${toDelete.id}`, { params: { remove_from_template: fromTemplate } }), "Item removido"); setToDelete(null); }} />
      <ConfirmRemoveDialog open={!!sectionToDelete} testId="delete-section" title={`Remover categoria "${sectionToDelete?.name}"?`}
        description={`A categoria e seus ${sectionToDelete?.items.length || 0} item(ns) saem do checklist deste show.`}
        onCancel={() => setSectionToDelete(null)}
        onConfirm={(fromTemplate) => { run(() => api.delete(`${base}/sections`, { params: { name: sectionToDelete.name, remove_from_template: fromTemplate } }), "Categoria removida"); setSectionToDelete(null); }} />
      <NameDialog open={addSection} onOpenChange={setAddSection} testId="add-section" title="Nova categoria" label="Nome da categoria"
        submitLabel="Adicionar" templateLabel="Incluir também nos próximos shows"
        onSubmit={({ name, template }) => run(() => api.post(`${base}/sections`, { name, template }), "Categoria adicionada")} />
    </div>
  );
}
